import { clampPct } from "./theme"
import { formatAmount } from "../constants"
import { StatusPill, MoneyText, EmptyState as DsEmpty } from "procurement/design"

// ── Button ─────────────────────────────────────────────────────────────────
// variant: primary | secondary | ghost | warn. Renders <a> when `href`/`as`
// isn't needed — here we stick to <button> for form actions and nav callbacks.
export const Button = ({
  variant = "secondary",
  size,
  block,
  leftIcon,
  rightIcon,
  children,
  className = "",
  ...rest
}) => (
  <button
    type="button"
    className={[
      "pc-btn",
      `pc-btn--${variant}`,
      size === "sm" ? "pc-btn--sm" : "",
      block ? "pc-btn--block" : "",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
    // Deliberate pass-through: callers rely on forwarding onClick/disabled/
    // type/style/aria-* etc. across the whole procurement UI.
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...rest}
  >
    {leftIcon}
    {children}
    {rightIcon}
  </button>
)

// ── Checkbox ───────────────────────────────────────────────────────────────
// Plain, token-styled checkbox for row selection. `onChange` receives the
// checked boolean directly (not the event) to match the rest of the kit's
// field callbacks (see Select in fields.js).
export const Checkbox = ({ checked, onChange, label, ...rest }) => (
  <label className="pc-checkbox" onClick={(e) => e.stopPropagation()}>
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange?.(e.target.checked)}
      // eslint-disable-next-line react/jsx-props-no-spreading -- see Button above
      {...rest}
    />
    {label && <span>{label}</span>}
  </label>
)

// ── StatusBadge ────────────────────────────────────────────────────────────
// One badge for every procurement status. Colour + label come from STATUS_META
// so New/Pending/Approved/Info Requested/Rejected/Closed stay consistent
// everywhere they appear. `label`/`tone` allow ad-hoc overrides (e.g. "Proposed").
export const StatusBadge = ({ status, label }) => (
  <StatusPill status={status} label={label} />
)

// ── Currency ───────────────────────────────────────────────────────────────
// Thousand-separated, tabular-figure VND. Right-aligned by default (currency
// columns). Pass `unit={false}` to drop the ₫ suffix in tight spots.
export const Currency = ({ value, unit = true, align = "right", strong, precise }) => (
  <MoneyText value={value} align={align} precise={precise} />
)

// Table-cell wrapper so `<Currency>` slots straight into a column `Cell`.
export const CurrencyCell = (props) => <Currency {...props} />

// ── ProgressBar ────────────────────────────────────────────────────────────
export const ProgressBar = ({ pct, color }) => (
  <div className="pc-progress" role="presentation">
    <span style={{ width: `${clampPct(pct)}%`, background: color }} />
  </div>
)

// ── EmptyState ─────────────────────────────────────────────────────────────
// Empty states guide the next action rather than just declaring "no data".
export const EmptyState = (props) => <DsEmpty {...props} />

// ── Alert / banner ─────────────────────────────────────────────────────────
export const Alert = ({ tone = "warn", icon, title, children, action }) => (
  <div className={`pc-alert pc-alert--${tone}`} role="status">
    {icon && <span className="pc-alert-ico">{icon}</span>}
    <div className="pc-alert-body">
      {title && <div className="pc-alert-title">{title}</div>}
      {children && <div className="pc-alert-text">{children}</div>}
    </div>
    {action && <div style={{ flex: "none" }}>{action}</div>}
  </div>
)

// ── Loading ────────────────────────────────────────────────────────────────
export const Loading = ({ label = "Loading…" }) => (
  <div className="pc-loading">{label}</div>
)

// ── Stat tile ──────────────────────────────────────────────────────────────
export const Stat = ({ label, value }) => (
  <div className="pc-stat">
    <div className="pc-stat-label">{label}</div>
    <div className="pc-stat-value">{value}</div>
  </div>
)

// Re-export the currency string helper for non-component contexts.
export { formatAmount }
